'use client';

import type { HintRow } from '@repo/ui/components/info-label';
import { Metric, type MetricStatus } from '@repo/ui/components/metric';
import { Skeleton } from '@repo/ui/components/skeleton';
import { AnimatedValue } from '@/components/animated-value';

import { isSameBenchmark } from '@/lib/benchmarks';
import { dayOf, type InvestorPerformance } from '@/lib/investor-analytics';
import type {
  InvestorFeesResponse,
  InvestorHoldingsResponse,
  InvestorPnlResponse,
  InvestorRiskMetrics
} from '../types/api';
import { formatBasisPoints } from '@/lib/format/percent/basis-points';
import { formatPercent } from '@/lib/format/percent/percent';
import { usdCents } from '@/lib/format/usd/cents';
import { formatCompactUsd } from '@/lib/format/usd/compact-usd';
import { formatSignedCompactUsd } from '@/lib/format/usd/signed-compact-usd';
import { formatUsdFigure } from '@/lib/format/usd/usd-figure';
import { netOfPendingLine } from '@/lib/net-of-pending';
import {
  formatHoldingsTotal,
  valuationNotes,
  valueHoldings,
  type CurrencyRegistry
} from '../utils/holding-format';

interface PortfolioMetricsProps {
  periodLabel: string;
  holdings?: InvestorHoldingsResponse;
  /** Names for strategy currency addresses, resolved by the view. */
  currencies?: CurrencyRegistry;
  metrics?: InvestorRiskMetrics;
  performance?: InvestorPerformance;
  pnl?: InvestorPnlResponse;
  fees?: InvestorFeesResponse;
  /**
   * The index the Net return card compares against: the first one the
   * reader selected. Matched to the served series by symbol, never by its
   * position in the response.
   */
  benchmark?: { symbol: string; name: string };
  holdingsLoading?: boolean;
  holdingsError?: boolean;
  metricsLoading?: boolean;
  metricsError?: boolean;
  pnlLoading?: boolean;
  pnlError?: boolean;
  feesLoading?: boolean;
  feesError?: boolean;
}

const LIVE_STATUS: MetricStatus = { label: 'Live', live: true };

/**
 * A served timestamp's calendar day as served (its `YYYY-MM-DD` prefix, the
 * analytics layer's `dayOf`), never moved across midnight by an offset.
 */
const servedDay = (iso: string | null | undefined): string | null =>
  iso == null ? null : dayOf(iso);

/** Why the net return and P&L ÷ deposits differ, in the reader's words. */
const FROM_FIRST_DEPOSIT =
  'Counted from the end of your first deposit day, so it can differ from P&L ÷ deposits';

/** The net return and its twin read in the same places. */
const NET_RETURN_FORMAT = { signed: true, fractionDigits: 1 } as const;

/** Notes under a card's definition, one per line, in its hover panel. */
function hintLines(lines: (string | null | undefined)[]): React.ReactNode {
  const shown = lines.filter((line): line is string => Boolean(line));
  if (shown.length === 0) return undefined;
  return shown.map((line, index) => (
    <p key={line} className={index > 0 ? 'mt-1.5' : undefined}>
      {line}
    </p>
  ));
}

/** "Label: value" from the shared net-of-pending line, as a hover row. */
function labelledRow(line: string | null): HintRow | null {
  if (line == null) return null;
  const at = line.indexOf(': ');
  return at < 0
    ? null
    : { label: line.slice(0, at), value: line.slice(at + 2) };
}

const usd2 = (value: string) =>
  formatUsdFigure(value, { maximumFractionDigits: 2 });

function metricValue(
  value: string | null,
  isLoading: boolean,
  isError: boolean
) {
  if (isLoading) return <Skeleton className="h-9 w-28" />;
  if (isError)
    return <span className="text-rowa-text-tertiary">Unavailable</span>;
  if (value == null) return <span className="text-rowa-text-tertiary">—</span>;
  return <AnimatedValue value={value} />;
}

/** Signed decimal string → "+$1.2M" / "−$450" with a true minus. */
function signedUsdOrNull(value: string | null | undefined): string | null {
  if (value == null) return null;
  const amount = Number(value);
  if (!Number.isFinite(amount)) return null;
  return formatSignedCompactUsd(amount, {
    maximumFractionDigits: 2,
    plus: true
  });
}

/**
 * The window's value change from the capital account the page already
 * loads: closing minus opening (opening is $0 when the window predates the
 * first deposit). It includes the money moved in and out, so the line says
 * how much of it that was: "Δ YTD +$717.27 · $704 deposited". A window with
 * no flows is the gain alone: "Δ 1W +$1.77".
 */
function windowChange(pnl: InvestorPnlResponse, periodLabel: string): string {
  // Exact cents end to end; a number only at the formatter.
  const change = usdCents(pnl.closingValueUsd) - usdCents(pnl.openingValueUsd);
  const flows = [
    usdCents(pnl.contributionsUsd) > 0n
      ? `${formatUsdFigure(pnl.contributionsUsd, { maximumFractionDigits: 2 })} deposited`
      : null,
    usdCents(pnl.withdrawalsUsd) > 0n
      ? `${formatUsdFigure(pnl.withdrawalsUsd, { maximumFractionDigits: 2 })} withdrawn`
      : null
  ].filter((flow): flow is string => flow != null);
  return [
    `Δ ${periodLabel} ${formatSignedCompactUsd(Number(change) / 100, { maximumFractionDigits: 2, plus: true })}`,
    ...(flows.length > 0 ? [flows.join(', ')] : [])
  ].join(' · ');
}

function seriesReturn(series: { value: number }[] | undefined): number | null {
  if (!series || series.length < 2) return null;
  const first = series[0]?.value;
  const last = series.at(-1)?.value;
  if (first == null || last == null || first === 0) return null;
  return last / first - 1;
}

/**
 * The four money headline cards: NAV (now + Δ over the window), net return
 * vs benchmark (with the money weighted figure when served), windowed net
 * P&L from the roll forward identity, and realized fees with the expense
 * ratio. Risk-adjusted statistics (Sharpe / Sortino, drawdown) live on the
 * Risk screen.
 */
export function PortfolioMetrics({
  periodLabel,
  holdings,
  currencies,
  metrics,
  performance,
  pnl,
  fees,
  benchmark,
  holdingsLoading = false,
  holdingsError = false,
  metricsLoading = false,
  metricsError = false,
  pnlLoading = false,
  pnlError = false,
  feesLoading = false,
  feesError = false
}: PortfolioMetricsProps) {
  // The headline on the same basis as the positions beneath it: the sum of
  // the converted rows when the whole wallet is in the response, the served
  // total when it spans more than one page, with a line beside it whenever
  // that number is not every holding at its dollar value.
  const valuation = holdings ? valueHoldings(holdings, currencies) : null;
  const navValue = valuation ? formatHoldingsTotal(valuation) : null;
  const navNotes = valuation ? valuationNotes(valuation) : [];
  // Mirrors the served coverage block: value-weighted priced share, the
  // holding count behind it, and a flag when the external wallet scan is
  // not contributing (the one source that can drop out).
  const staleNote =
    holdings &&
    !holdings.coverage.externalIncluded &&
    holdings.coverage.externalStatus !== 'disabled'
      ? ' · 1 source stale'
      : '';
  // The headline's population, in plain words. 'wallet-merged' means the
  // external wallet scan contributed to these totals — say so where the
  // stale note already lives, so the reader knows what the number covers.
  const scopeNote =
    holdings?.populationScope === 'wallet-merged'
      ? ' · includes external wallet holdings'
      : '';
  // A state the reader needs stays on the card; how a priced total was
  // put together goes behind the hover.
  const pricedState =
    !holdings || holdings.coverage.pricedBps != null
      ? undefined
      : holdings.coverage.holdingsTotal === 0
        ? 'No positions yet'
        : 'Waiting on pricing data';
  const navRows: HintRow[] =
    holdings?.coverage.pricedBps == null
      ? []
      : [
          {
            label: 'Share of value priced',
            value: formatBasisPoints(holdings.coverage.pricedBps, {
              fractionDigits: 0
            })
          },
          {
            label: 'Holdings priced',
            value: `${holdings.coverage.pricedCount} of ${holdings.coverage.holdingsTotal}`
          }
        ];
  const navHint = hintLines([
    'What your holdings are worth right now, at their latest prices. The change over the period includes money you put in and took out, so it is not your gain. Net P&L is.',
    staleNote ? 'One data source is out of date.' : null,
    scopeNote ? 'Includes holdings in your external wallet.' : null,
    ...navNotes
  ]);

  /**
   * The NAV card pairs its `/holdings` headline with a window change ONLY
   * when the API says both describe the same population.
   *
   * v1.89.0 (BE-000) added `populationScope` to `/holdings`, decided per
   * request: 'wallet-merged' when the external wallet scan contributed to
   * the searched population, 'rowa-managed' otherwise. The capital account
   * (`/pnl`) is always rowa-managed. So:
   *
   * - 'rowa-managed': headline and capital account cover the same
   *   population, and the window change is stated: raw value change over
   *   the window, flows included, which is exactly what a NAV movement is.
   * - 'wallet-merged', unknown, or absent (pre-1.89): value only. A merged
   *   headline with a rowa-managed change figure is not a like-for-like
   *   comparison — the mismatch that once printed a delta two orders of
   *   magnitude away from its own headline. The provenance line names the
   *   merged population instead.
   *
   * The change runs from the window's OPENING value (`windowChange`), never
   * from the first funded day of the value series: that anchor mixed later
   * deposits with gains (QA 2026-10-04: "Δ YTD +$109.62" on a window that
   * opened at $0 and closed at $717.27 with $704 deposited).
   *
   * The anchor-relative percentage stays retired in every arm: dividing a
   * flow-inclusive delta by the first funded day's balance printed
   * "+189,217%" on a live wallet. The flow-adjusted return is the Net
   * return card's job, from the served `windowReturnBps`.
   */
  const navDescription =
    holdings?.populationScope === 'rowa-managed' &&
    // /pnl states its own envelope scope; both sides must say so.
    pnl?.populationScope === 'rowa-managed' &&
    !holdingsError
      ? windowChange(pnl, periodLabel)
      : `Recognized value · ${valuation?.total.basis === 'served' ? valuation.servedCurrency : 'USD'}`;

  const windowReturnBps = metrics?.windowReturnBps ?? null;
  // The served series for the selected index. The response orders its
  // benchmarks its own way, so pairing the selected name with the first
  // series by position printed one index's return under another's name.
  const servedBenchmark =
    benchmark == null
      ? undefined
      : performance?.benchmarks.find((entry) =>
          isSameBenchmark(benchmark.symbol, entry.symbol)
        );
  const benchmarkReturn = seriesReturn(servedBenchmark?.series);
  const excessPp =
    windowReturnBps != null && benchmarkReturn != null
      ? windowReturnBps / 100 - benchmarkReturn * 100
      : null;
  // The money weighted figure prices the investor's own deposit timing —
  // it legitimately differs from the TWR, which is the point of both.
  const moneyWeightedBps = metrics?.moneyWeightedReturnBps ?? null;
  const returnBasis =
    excessPp != null && benchmark
      ? `${periodLabel} · ${formatPercent(excessPp, { digits: 1, sign: 'signed', unit: ' pp' })} vs ${benchmark.name}`
      : `${periodLabel} · deposits and withdrawals excluded`;
  // API 1.159.0: the same return with the fees still building up taken
  // off too. A second line under the basis, never in the return's place
  // and never in the excess over the index above.
  const afterPendingFees =
    windowReturnBps == null
      ? null
      : netOfPendingLine(
          metrics?.windowReturnNetOfPendingBps,
          metrics?.windowReturnNetOfPendingReason,
          (bps) => formatBasisPoints(bps, NET_RETURN_FORMAT)
        );

  // The return counts no change on a first deposit's own day: there is no
  // value before it to measure from. Net P&L counts that day's change, so
  // the two cannot be reconciled by P&L ÷ deposits, and the card says so
  // when the window holds the account's first deposit. Read from the
  // served first deposit and the resolved window, never inferred from the
  // P&L: an account emptied and refilled also opens a window at $0.
  const firstDepositDay = servedDay(performance?.firstDepositAt);
  const windowFromDay = servedDay(metrics?.windowFrom);
  const windowToDay = servedDay(metrics?.windowTo);
  const fromFirstDeposit =
    windowReturnBps != null &&
    firstDepositDay != null &&
    windowFromDay != null &&
    firstDepositDay >= windowFromDay &&
    (windowToDay == null || firstDepositDay <= windowToDay)
      ? FROM_FIRST_DEPOSIT
      : null;
  const returnRows = [
    moneyWeightedBps != null
      ? {
          label: 'Money weighted',
          value: formatBasisPoints(moneyWeightedBps, {
            signed: true,
            fractionDigits: 1
          })
        }
      : null,
    labelledRow(afterPendingFees),
    metrics && metrics.windowDays > 0
      ? { label: 'Days measured', value: String(metrics.windowDays) }
      : null
  ].filter((row): row is HintRow => row != null);
  // An unlabelled pending line (no "label: value" shape) stays a sentence.
  const returnHint = hintLines([
    'How your investments did over the period, with money you put in and took out left out. It is after the fees charged so far.',
    afterPendingFees != null && labelledRow(afterPendingFees) == null
      ? afterPendingFees
      : null,
    fromFirstDeposit
  ]);

  // Windowed net P&L from the roll forward identity:
  // closing = opening + net flow + net P&L, exact on the served strings.
  const netPnl = pnl != null ? Number(pnl.netPnlUsd) : null;
  const pnlValue =
    netPnl != null
      ? formatSignedCompactUsd(netPnl, {
          maximumFractionDigits: 2,
          plus: true
        })
      : null;
  // The realized and unrealized halves, shown the day the backend serves
  // them. Until then the card says the two are reported together.
  const realized = signedUsdOrNull(pnl?.realizedPnlUsd);
  const unrealized = signedUsdOrNull(pnl?.unrealizedPnlUsd);
  const pnlSplit =
    realized != null && unrealized != null
      ? `Realized ${realized} · unrealized ${unrealized}`
      : null;
  const pnlSplitPending = pnl != null && pnlSplit == null;
  // The roll forward behind the figure: opening, money in and out, closing.
  const pnlRows: HintRow[] =
    pnl == null
      ? []
      : [
          { label: 'Opening value', value: usd2(pnl.openingValueUsd) },
          { label: 'Put in', value: usd2(pnl.contributionsUsd) },
          { label: 'Taken out', value: usd2(pnl.withdrawalsUsd) },
          { label: 'Closing value', value: usd2(pnl.closingValueUsd) },
          ...(pnlValue != null
            ? [{ label: 'Net P&L', value: pnlValue, total: true }]
            : [])
        ];

  const feeTotal = fees != null ? Number(fees.accrued.totalUsd) : null;
  const feeValue =
    feeTotal != null
      ? formatCompactUsd(feeTotal, { maximumFractionDigits: 2 })
      : null;
  // Every fee type the period charged, then what they cost as a share of
  // the money invested. Protocol and curator fees show only when charged.
  const feeRows: HintRow[] =
    fees == null
      ? []
      : [
          { label: 'Management', value: usd2(fees.accrued.managementUsd) },
          { label: 'Performance', value: usd2(fees.accrued.performanceUsd) },
          { label: 'Entry', value: usd2(fees.accrued.entryUsd) },
          { label: 'Exit', value: usd2(fees.accrued.exitUsd) },
          ...(usdCents(fees.accrued.protocolUsd) > 0n
            ? [{ label: 'Protocol', value: usd2(fees.accrued.protocolUsd) }]
            : []),
          ...(fees.accrued.curatorUsd != null &&
          usdCents(fees.accrued.curatorUsd) > 0n
            ? [{ label: 'Curator', value: usd2(fees.accrued.curatorUsd) }]
            : []),
          ...(fees.windowExpenseBps != null
            ? [
                {
                  label: 'Expense ratio',
                  value: formatPercent(fees.windowExpenseBps, {
                    from: 'bps',
                    digits: 2
                  })
                },
                {
                  label: 'Per year',
                  value:
                    fees.annualizedExpenseBps != null
                      ? formatPercent(fees.annualizedExpenseBps, {
                          from: 'bps',
                          digits: 2
                        })
                      : 'After 30 days'
                }
              ]
            : []),
          ...(feeValue != null
            ? [{ label: 'Total fees', value: feeValue, total: true }]
            : [])
        ];

  return (
    <section
      aria-label="Portfolio headline metrics"
      className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4"
    >
      <Metric
        title="Net asset value"
        value={metricValue(navValue, holdingsLoading, holdingsError)}
        description={navDescription}
        provenance={pricedState}
        hint={holdingsError ? undefined : navHint}
        hintRows={holdingsError ? undefined : navRows}
        status={holdingsError ? undefined : LIVE_STATUS}
      />
      <Metric
        title="Net return"
        value={metricValue(
          windowReturnBps == null
            ? null
            : formatBasisPoints(windowReturnBps, NET_RETURN_FORMAT),
          metricsLoading,
          metricsError
        )}
        valueTone={
          windowReturnBps != null && windowReturnBps < 0 ? 'error' : undefined
        }
        description={metricsError ? undefined : returnBasis}
        // After the fees CHARGED so far, never "net of fees": vault fees
        // are charged from time to time by minting fee shares, so dilution
        // lands in the share price only when a fee is charged. Fees still
        // building up are not in the daily value series behind the TWR.
        // windowDays counts daily NAV observations (weekends included —
        // crypto has no trading days), clamped to available history.
        hint={metricsError ? undefined : returnHint}
        hintRows={metricsError ? undefined : returnRows}
        status={metricsError ? undefined : { label: 'Calculated' }}
      />
      <Metric
        title="Net P&L"
        value={metricValue(pnlValue, pnlLoading, pnlError)}
        valueTone={netPnl != null && netPnl < 0 ? 'error' : undefined}
        description={
          pnlSplit != null
            ? `${periodLabel} · ${pnlSplit}`
            : `${periodLabel} · deposits and withdrawals excluded`
        }
        hint={
          pnlError
            ? undefined
            : 'What you gained or lost over the period. Money you put in or took out is not a gain or a loss. It is after the fees charged so far.'
        }
        hintRows={pnlError ? undefined : pnlRows}
        status={
          pnlError
            ? undefined
            : pnlSplitPending
              ? {
                  label: 'Live',
                  live: true,
                  hint: 'Realized and unrealized together. The split arrives with cost basis accounting.'
                }
              : LIVE_STATUS
        }
      />
      <Metric
        title="Fees"
        value={metricValue(feeValue, feesLoading, feesError)}
        description={`${periodLabel} · all fees included`}
        hint={
          feesError
            ? undefined
            : 'Every fee you paid over the period, entry and exit fees included.'
        }
        hintRows={feesError ? undefined : feeRows}
        status={feesError ? undefined : LIVE_STATUS}
      />
    </section>
  );
}
